import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { ScrollGallery, type GalleryImage } from '../components/ScrollGallery.js';
import { CorridorScrub } from '../components/CorridorScrub.js';
import { LivingSculpt } from '../components/LivingSculpt.js';
import { Scene, Parallax } from '../components/Cinematic.js';
import { useLenis } from '../lib/useLenis.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';
import { currentVideoTier } from '../lib/videoTier.js';
import { MagneticButton } from '../components/MagneticButton.js';
import { cn } from '../lib/classnames.js';
import {
  detectedWalletNames,
  hasWallet,
  signInWithEthereum,
  signInWithWalletConnect,
  walletConnectAvailable,
  waitForInjectedWallet,
  SiweDiagnosticError,
  SiweUserRejected,
} from '../web3/siwe.js';

interface ErrorState {
  message: string;
  diagnostic: string | null;
}

function noWalletMessage(): string {
  return walletConnectAvailable()
    ? 'No browser wallet detected. Tap Connect Wallet for a QR code you can scan with your phone’s wallet, or install MetaMask, Brave Wallet, or Rabby and refresh.'
    : 'No wallet detected. Install MetaMask, Brave Wallet, Rabby, or another browser wallet, then refresh.';
}

export function Landing() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  // While a WalletConnect signature is pending on the user's phone, surface a cue
  // (the QR modal only covers the connection, not the sign request that follows).
  const [pending, setPending] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });
  if (meQuery.data?.authenticated) {
    // Holders go to the app; non-holders to the early-access gate.
    navigate(meQuery.data.holder ? '/dashboard' : '/early-access', { replace: true });
  }

  // The hero gallery is filled with the live feed's published pieces.
  const feedQuery = useQuery({
    queryKey: ['feed', 'hero'],
    queryFn: () => api.feed(12),
    staleTime: 5 * 60_000,
  });
  const galleryImages: GalleryImage[] = useMemo(() => {
    const items = (feedQuery.data?.items ?? []) as {
      thumbnail?: string;
      aspectRatio?: string;
    }[];
    return items
      .filter((i) => !!i.thumbnail)
      .map((i) => {
        const [w, h] = (i.aspectRatio ?? '1:1').split(':').map(Number);
        return { url: i.thumbnail as string, aspect: (w || 1) / (h || 1) };
      });
  }, [feedQuery.data]);

  // The hero manifest (/hero) returns either the scroll-scrubbed VIDEO frame
  // sequence (~75 frames, preferred) or the still-frame corridor. We deliberately
  // do NOT seed initialData: the sequence count is dynamic, and a stale still-URL
  // seed would flash the wrong (incoherent) crossfade before the real manifest
  // lands. While it's pending, the render shows a neutral dark hold (below), not
  // the editorial fallback — so there's no ugly flash either way.
  const heroQuery = useQuery({
    queryKey: ['hero'],
    queryFn: () => api.hero(),
    staleTime: 10 * 60_000,
  });
  // Serve the lighter ~1024px corridor frame set (hero/seq-v6-720, ~6MB) to
  // phones / constrained connections instead of the full 1664px set (~17MB).
  // The dolly draws to a viewport-sized canvas, where 1024px is retina on a
  // phone. Tier resolved once — pointer type doesn't change mid-session.
  const [heroTier] = useState(currentVideoTier);
  const rawHeroUrls: string[] = heroQuery.data?.urls ?? [];
  const heroImages: string[] = useMemo(
    () =>
      heroTier === 'mobile'
        ? rawHeroUrls.map((u) => u.replace('/hero/seq-v6/', '/hero/seq-v6-720/'))
        : rawHeroUrls,
    [rawHeroUrls, heroTier],
  );

  // The curated trio of gildings shown as living sculpts under the three beats.
  const showcaseQuery = useQuery({
    queryKey: ['showcase'],
    queryFn: () => api.showcase(),
    staleTime: 10 * 60_000,
  });
  const showcaseItems: ShowcaseItem[] = showcaseQuery.data?.items ?? [];

  // Which beat's sculpt is in view (the only one that actively renders), and
  // which beats have been approached (mounted-but-parked once near, so they
  // resume instantly without a build hitch). Driven imperatively from scroll.
  const [activeBeat, setActiveBeat] = useState(-1);
  const activeBeatRef = useRef(-1);
  const [nearBeats, setNearBeats] = useState<boolean[]>([false, false, false]);
  const nearBeatsRef = useRef<boolean[]>([false, false, false]);

  // Buttery smooth scroll (Lenis) — the foundation the scrubbed hero rides on.
  useLenis();

  // The headline lines reveal behind an `overflow: hidden` clip (so the text
  // rises out of nothing). That clip is only needed DURING the rise — left on, it
  // slices the text's drop-shadow into a hard horizontal line at rest. So once
  // each line's animation settles, release the clip and let the shadow fade off.
  useEffect(() => {
    const lines = Array.from(document.querySelectorAll<HTMLElement>('.gild-line'));
    if (lines.length === 0) return;
    const release = (line: HTMLElement) => (): void => {
      line.style.overflow = 'visible';
    };
    const teardown = lines.map((line) => {
      const done = release(line);
      const inner = line.querySelector('.gild-line__inner');
      inner?.addEventListener('animationend', done, { once: true });
      // Fallback if animationend never fires (reduced-motion / already settled).
      const t = window.setTimeout(done, 1600);
      return () => {
        inner?.removeEventListener('animationend', done);
        window.clearTimeout(t);
      };
    });
    return () => teardown.forEach((fn) => fn());
  }, []);

  // The hero is a scroll-scrubbed walk through photoreal corridor renders, with
  // the headline, the three feature beats, and the CTA all surfacing and
  // dissolving in turn AS YOU SCROLL — one continuous sequence. Reduced-motion
  // or not-yet-generated renders fall back to the static hero + reel + features.
  const reduced = useReducedMotion();
  // A video frame-sequence (≥24 frames) plays as a true dolly (dense mode).
  // Anything fewer is still imagery — drive it from the single master via the
  // coherent continuous zoom (passing every still would crossfade incoherent
  // renders). So: many frames → play them all; few → just the entrance frame.
  const heroFrames: string[] =
    heroImages.length >= 24 ? heroImages : heroImages.slice(0, 1);
  const useScrub = !reduced && heroFrames.length >= 1;

  // Overlay fades driven imperatively from scroll progress (no per-frame
  // re-render). Each beat rises in, holds, then recedes as the next arrives.
  const headlineRef = useRef<HTMLDivElement>(null);
  const beat1Ref = useRef<HTMLDivElement>(null);
  const beat2Ref = useRef<HTMLDivElement>(null);
  const beat3Ref = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const colophonRef = useRef<HTMLDivElement>(null);
  const handleProgress = (p: number): void => {
    const headline = headlineRef.current;
    if (headline) {
      const o = clamp01(1 - smooth(0.05, 0.15, p));
      headline.style.opacity = String(o);
      // Rack-focus recede: as the title leaves it lifts, eases back, and pulls
      // out of focus — a lens letting go of the subject, not a flat dissolve.
      const scale = (0.97 + 0.03 * o).toFixed(3);
      headline.style.transform = `translateY(${(-26 * (1 - o)).toFixed(1)}px) scale(${scale})`;
      headline.style.filter = o < 0.995 ? `blur(${((1 - o) * 6).toFixed(2)}px)` : 'none';
    }
    driveBeat(beat1Ref.current, p, 0.18, 0.25, 0.33, 0.4);
    driveBeat(beat2Ref.current, p, 0.42, 0.49, 0.57, 0.64);
    driveBeat(beat3Ref.current, p, 0.66, 0.72, 0.79, 0.85);

    // Light up exactly the in-view beat's living sculpt; keep the others parked.
    // `near` is sticky — once a beat is approached it stays mounted (paused),
    // so scrolling back to it resumes with no rebuild. Only one ever draws.
    let active = -1;
    let nearChanged = false;
    const near = nearBeatsRef.current.slice();
    BEAT_BANDS.forEach(([a, d], i) => {
      if (p >= a && p <= d) active = i;
      if (!near[i] && p >= a - 0.1 && p <= d + 0.05) {
        near[i] = true;
        nearChanged = true;
      }
    });
    if (active !== activeBeatRef.current) {
      activeBeatRef.current = active;
      setActiveBeat(active);
    }
    if (nearChanged) {
      nearBeatsRef.current = near;
      setNearBeats(near);
    }
    const cta = ctaRef.current;
    if (cta) {
      const o = clamp01(smooth(0.85, 0.96, p));
      cta.style.opacity = String(o);
      cta.style.transform = `translateY(${(18 * (1 - o)).toFixed(1)}px)`;
      cta.style.pointerEvents = o > 0.5 ? 'auto' : 'none';
      // Arrival payoff — once the card is materially visible, fire the one-shot
      // gilded-edge trace + the 'Start here.' gild (CSS keyed to .is-arrived).
      if (o > 0.6 && !cta.classList.contains('is-arrived')) cta.classList.add('is-arrived');
    }
    const colophon = colophonRef.current;
    if (colophon) {
      colophon.style.opacity = String(clamp01(smooth(0.9, 0.99, p)));
    }
  };

  const onConnect = async (method: 'injected' | 'walletconnect' = 'injected'): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setShowDiagnostic(false);
    setPending(null);
    try {
      const out =
        method === 'walletconnect'
          ? // Defer the nonce until the WC session is live (see siwe.ts). On desktop
            // the sign request is relayed silently to the phone, so prompt the user
            // to reopen their wallet once we're waiting on the signature.
            await signInWithWalletConnect(async () => (await api.nonce()).nonce, {
              onAwaitingSignature: () =>
                setPending('Connected. Open your wallet app and approve the signature to finish.'),
            })
          : await signInWithEthereum((await api.nonce()).nonce);
      setPending(null);
      const res = await api.verify(out.message, out.signature);
      // Claim a pending anonymous free gild into the wallet — holders AND
      // non-holders keep their one pre-sign-in gild (best-effort, idempotent).
      try {
        const pendingFree = window.localStorage.getItem('gilding_free_pending');
        if (pendingFree) {
          await api.claimFreeGild(pendingFree);
          window.localStorage.removeItem('gilding_free_pending');
        }
      } catch {
        /* claim is non-critical; the marker stays so a later sign-in retries */
      }
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate(res.holder ? '/dashboard' : '/early-access');
    } catch (e) {
      console.warn('siwe failed', e);
      setPending(null);
      setError(buildErrorState(e));
      setBusy(false);
    }
  };

  // Arriving from the header's quick sign-in (no injected wallet → it hands the
  // desktop QR flow here, `?signin=wc`): auto-start the WalletConnect connection so
  // the visitor lands straight on the QR + the awaiting-signature cue. One-shot —
  // strip the param first so a re-render / back-nav can't re-trigger it.
  const autoConnectFired = useRef(false);
  useEffect(() => {
    if (autoConnectFired.current) return;
    if (searchParams.get('signin') !== 'wc') return;
    autoConnectFired.current = true;
    setSearchParams({}, { replace: true });
    void onConnect('walletconnect');
    // (deliberately depends only on searchParams — onConnect/setSearchParams are
    // stable enough for this one-shot; react-hooks lint isn't configured here)
  }, [searchParams]);

  const buildErrorState = (e: unknown): ErrorState => {
    if (e instanceof SiweUserRejected) {
      return {
        message: 'Signature cancelled in your wallet. Tap Connect Wallet to try again.',
        diagnostic: null,
      };
    }
    if (e instanceof SiweDiagnosticError) {
      return {
        message: friendlySiweMessage(e),
        diagnostic: JSON.stringify(
          {
            stage: e.stage,
            message: e.message,
            detail: e.detail,
            wallets: detectedWalletNames(),
            origin: typeof window !== 'undefined' ? window.location.origin : null,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          },
          null,
          2,
        ),
      };
    }
    const err = e as { code?: string; message?: string } | undefined;
    const code = err?.code;
    const detail = err?.message;
    if (code === 'unauthenticated' && detail === 'invalid_signature') {
      return {
        message:
          'Wallet signature did not verify. Make sure your wallet is on Ethereum mainnet (chain 1) and try again.',
        diagnostic: null,
      };
    }
    if (code === 'unauthenticated' && detail === 'nonce_expired') {
      return {
        message:
          'Sign-in window expired. Tap Connect Wallet again. A fresh nonce will be issued.',
        diagnostic: null,
      };
    }
    if (code === 'bad_request') {
      return {
        message: 'Sign-in payload rejected by the server. Refresh the page and try again.',
        diagnostic: detail ? `server: ${detail}` : null,
      };
    }
    if (!hasWallet()) {
      return { message: noWalletMessage(), diagnostic: null };
    }
    return {
      message: `Sign-in failed${detail ? `: ${detail}` : ''}.`,
      diagnostic: JSON.stringify(
        {
          message: detail ?? String(e),
          code,
          name: (err as { name?: string })?.name ?? null,
          wallets: detectedWalletNames(),
        },
        null,
        2,
      ),
    };
  };

  const friendlySiweMessage = (e: SiweDiagnosticError): string => {
    switch (e.stage) {
      case 'no_wallet':
        return 'No wallet provider was found. Open this page from inside your wallet’s in-app browser.';
      case 'eth_request_accounts':
        return 'Your wallet wouldn’t share an account. Unlock it and tap Connect Wallet again.';
      case 'no_account':
        return 'Your wallet did not return an account. Try selecting an account in your wallet, then tap Connect Wallet.';
      case 'create_client':
        return 'Could not initialize the wallet connection. Reload and try again.';
      case 'sign_message':
        return 'Your wallet rejected the signature request. See the detail below. Usually this is a chain mismatch or a stale session.';
      default:
        return `Sign-in failed: ${e.message}`;
    }
  };

  const handleConnectClick = (): void => {
    void (async () => {
      // Wallet in-app browsers can inject their provider after page load —
      // wait briefly before concluding there's no wallet, so MetaMask/Trust
      // browsers always get the same in-wallet flow as before.
      if (hasWallet() || (await waitForInjectedWallet())) {
        void onConnect();
        return;
      }
      // Genuinely no injected wallet: fall through to the QR path when the
      // build carries WalletConnect, instead of dead-ending the visitor.
      if (walletConnectAvailable()) {
        void onConnect('walletconnect');
        return;
      }
      setError({ message: noWalletMessage(), diagnostic: null });
    })();
  };

  // The connect / free-gilding CTAs — shared by the corridor's arrival overlay
  // and the static fallback hero so both stay in lockstep.
  const ctaButtons = (
    <>
      {/* Primary path — connect. */}
      <div className="flex flex-col items-start gap-2">
        <MagneticButton variant="primary" busy={busy} onClick={handleConnectClick}>
          Connect Wallet
          <span aria-hidden="true">&rarr;</span>
        </MagneticButton>
        <div className="flex flex-col gap-1 pl-1 text-ink-subtle">
          <p className="font-mono text-caption">Sign-In With Ethereum. No gas, no email</p>
          {hasWallet() && (
            <p className="font-mono text-caption">{detectedWalletNames().join(' · ')} detected</p>
          )}
        </div>
      </div>

      {/* No-wallet path — the first gilding is free. */}
      <div className="flex flex-col items-start gap-2">
        <MagneticButton variant="gold-ghost" to="/new" pull={0.22}>
          <FreeSparkle className="text-accent transition-colors duration-300 group-hover:text-[#0b0a0f]" />
          Gild your first, free
          <span aria-hidden="true">&rarr;</span>
        </MagneticButton>
        <p className="pl-1 font-mono text-caption text-ink-subtle">No wallet, no sign-up.</p>
      </div>
    </>
  );

  // Awaiting-signature cue (WalletConnect): the QR modal closes on connect and
  // gives no hint for the sign request, so tell the user to reopen their wallet.
  const pendingBlock =
    pending && !error ? (
      <div
        className="max-w-md rounded-md border border-accent/40 bg-surface/95 p-5 shadow-2xl backdrop-blur"
        role="status"
        aria-live="polite"
      >
        <h2 className="font-display text-h-meta font-semibold text-accent">Almost there</h2>
        <p className="mt-2 text-body text-ink-soft">{pending}</p>
      </div>
    ) : null;

  // Connect-error alert, shared by both hero modes.
  const errorBlock = error ? (
    <div className="max-w-md rounded-md border border-danger/30 bg-surface/95 p-5 shadow-2xl backdrop-blur" role="alert">
      <h2 className="font-display text-h-meta font-semibold text-danger">Something didn&apos;t take</h2>
      <p className="mt-2 text-body text-ink-soft">{error.message}</p>
      {error.diagnostic && (
        <>
          <button
            type="button"
            className="btn-ghost mt-3"
            onClick={() => setShowDiagnostic((s) => !s)}
            aria-expanded={showDiagnostic}
          >
            <Chevron
              className={`h-3 w-3 transition-transform duration-200 ${showDiagnostic ? 'rotate-180' : ''}`}
            />
            {showDiagnostic ? 'Hide detail' : 'Show detail'}
          </button>
          {showDiagnostic && (
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-xs border border-hairline bg-surface-sunken p-3 font-mono text-caption leading-snug text-ink-subtle">
              {error.diagnostic}
            </pre>
          )}
        </>
      )}
    </div>
  ) : null;

  return (
    <>
      {useScrub ? (
        /* ===== HERO ===== A scroll-scrubbed walk through photoreal corridor
           renders. Headline → three feature beats → CTA all surface and dissolve
           in one continuous scroll, arriving at the doorway of light. */
        <>
          <CorridorScrub
            images={heroFrames}
            heightVh={560}
            onProgress={handleProgress}
            // Force dark-theme tokens across the whole hero: the corridor backdrop
            // is ALWAYS dark, so the overlay type must stay light in BOTH themes.
            // (In light mode the ink token flips dark → dark-on-dark, unreadable.)
            className="theme-dark z-0 ml-[calc(50%-50vw)] w-screen"
          >
            {/* Entrance headline — holds, then dissolves as you walk in. */}
            <div
              ref={headlineRef}
              className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center will-change-[opacity,transform]"
            >
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{
                    width: '150%',
                    height: '155%',
                    background: 'radial-gradient(closest-side, rgba(0,0,0,0.62), rgba(0,0,0,0.30) 55%, transparent 78%)',
                    filter: 'blur(8px)',
                  }}
                />
                <h1 className="relative heading-hero max-w-3xl text-balance text-ink drop-shadow-[0_3px_26px_rgba(0,0,0,0.95)]">
                  <span className="gild-line">
                    <span className="gild-line__inner">
                      Bring a <em className="font-display not-italic text-[#ffdf9e]">sketch</em>.
                    </span>
                  </span>
                  <span className="gild-line">
                    <span className="gild-line__inner" style={{ animationDelay: '150ms' }}>
                      Leave with{' '}
                      <GildWord className="font-display italic">gold</GildWord>.
                    </span>
                  </span>
                </h1>
                <div className="gild-underline" aria-hidden="true" />
                <p className="relative lede mx-auto mt-7 max-w-xl text-ink/95 drop-shadow-[0_2px_16px_rgba(0,0,0,0.98)]">
                  One line is enough. Claude gilds it into a richly textured prompt,
                  and the strongest image models render the result.
                </p>
                <p className="relative mt-10 flex items-center justify-center gap-2 font-mono text-caption uppercase tracking-[0.25em] text-ink/85">
                  <ScrollGlyph /> Scroll to walk the gallery
                </p>
              </div>
            </div>

            {/* Feature beats — each fades in at depth, then recedes. */}
            <BeatOverlay
              forwardRef={beat1Ref}
              numeral="I."
              heading="Amplified by Claude"
              body="Claude turns your one-line sketch into a two-hundred-word brief: composition, lighting, material, mood. The direction an art director writes, applied to every image you make."
              media={
                showcaseItems[0] && nearBeats[0] ? (
                  <SculptMedia item={showcaseItems[0]} paused={activeBeat !== 0} />
                ) : null
              }
            />
            <BeatOverlay
              forwardRef={beat2Ref}
              numeral="II."
              heading="Pick a look, not a model"
              body="Film-still photoreal, editorial illustration, posters with type that actually reads. Choose the style; the routing, the settings, and the engine are handled for you."
              media={
                showcaseItems[1] && nearBeats[1] ? (
                  <SculptMedia item={showcaseItems[1]} paused={activeBeat !== 1} />
                ) : null
              }
            />
            <BeatOverlay
              forwardRef={beat3Ref}
              numeral="III."
              heading="Yours to keep"
              body="Gildings save to your private gallery, post to the public feed when you choose, and can be remixed, letting others cast their own character into your scene, attribution carried through."
              media={
                showcaseItems[2] && nearBeats[2] ? (
                  <SculptMedia item={showcaseItems[2]} paused={activeBeat !== 2} />
                ) : null
              }
            />

            {/* Arrival — a gilded sign-in card, centred where the beats appeared. */}
            <div
              ref={ctaRef}
              style={{ opacity: 0 }}
              className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-6 will-change-[opacity,transform]"
            >
              <LoginCard busy={busy} onConnect={handleConnectClick} />
            </div>

            {/* Colophon — sits flush at the foot of the final frame (no blank gap). */}
            <div
              ref={colophonRef}
              style={{ opacity: 0 }}
              className="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-6 pb-7 will-change-[opacity]"
            >
              <Colophon />
            </div>
          </CorridorScrub>

          {/* Connect errors / the awaiting-signature cue surface as a fixed toast. */}
          {(error || pendingBlock) && (
            <div className="fixed inset-x-0 bottom-6 z-50 flex justify-center px-5">
              {errorBlock ?? pendingBlock}
            </div>
          )}
        </>
      ) : !reduced && heroQuery.isPending ? (
        /* Manifest still loading — a neutral dark hold, NEVER the editorial
           fallback, so the hero never flashes the old left-aligned layout. */
        <div aria-hidden="true" className="ml-[calc(50%-50vw)] h-[100svh] w-screen bg-surface" />
      ) : (
        /* Static fallback — clean editorial hero + a swipeable reel + features. */
        <>
          <div className="relative z-10 mx-auto w-full max-w-6xl px-5 pt-16 md:pt-28">
            <Scene className="max-w-2xl" stagger={0.16} y={54} start="top 96%">
              <h1 className="heading-hero" data-cinema>
                Bring a <em className="font-display not-italic text-accent-deep">sketch</em>.
                <br />
                Leave with <em className="foil-text font-display italic">gold</em>.
              </h1>
              <p className="lede mt-7 max-w-xl md:mt-9" data-cinema>
                One line is enough: your idea, rough and unfinished. Claude gilds
                it into a richly textured prompt, and the strongest image models
                render the result.
              </p>
              <div className="mt-9 flex flex-col items-start gap-6" data-cinema>
                {ctaButtons}
              </div>
            </Scene>
          </div>
          <ScrollGallery images={galleryImages} className="relative z-10 mt-10 md:mt-16" />

          <div className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-24 pt-20 md:pb-32 md:pt-28">
            {(error || pendingBlock) && <div className="mt-6">{errorBlock ?? pendingBlock}</div>}

            {/* Free-credits hook — a quiet promise banded between hairlines. */}
        <Scene as="aside" className="mt-24 flex items-center gap-5 md:mt-32" start="top 90%">
          <span className="hairline flex-1" data-cinema />
          <span className="font-display italic text-lede text-ink-soft" data-cinema>
            Five free gildings on the house.
          </span>
          <span className="hairline flex-1" data-cinema />
        </Scene>

        {/* ===== FEATURES ===== Three beats, each arriving as its own scene. */}
        <section className="mt-24 grid grid-cols-12 gap-x-6 gap-y-20 md:mt-32 md:gap-y-28">
          {/* I — wide column with a parallaxing quill */}
          <Scene as="article" className="col-span-12 md:col-span-7" stagger={0.1}>
            <div className="flex items-start gap-6 md:gap-8">
              <Parallax speed={0.4} className="hidden flex-none md:block">
                <QuillDiagram className="h-28 w-24" />
              </Parallax>
              <div>
                <p data-cinema className="font-display text-h-display italic leading-none text-accent-deep">
                  I.
                </p>
                <h2 data-cinema className="heading-section mt-4">
                  Amplified by Claude
                </h2>
                <p data-cinema className="mt-4 max-w-prose text-body leading-relaxed text-ink-soft">
                  <span className="float-left mr-3 mt-2 font-display text-[3.5rem] font-semibold leading-[0.8] text-accent-deep">
                    C
                  </span>
                  laude turns your one-line sketch into a two-hundred-word brief:
                  composition, lighting, material, mood. The kind of direction an
                  art director writes, applied to every image you make.
                </p>
              </div>
            </div>
          </Scene>

          {/* II — narrow column, illuminated numeral only */}
          <Scene as="article" className="col-span-12 md:col-span-5" stagger={0.1}>
            <p data-cinema className="font-display text-h-display italic leading-none text-accent-deep">
              II.
            </p>
            <h2 data-cinema className="heading-section mt-4">
              Pick a look, not a model
            </h2>
            <p data-cinema className="mt-4 max-w-prose text-body leading-relaxed text-ink-soft">
              Film-still photoreal, editorial illustration, posters with type
              that actually reads. Choose the style; the routing, the settings,
              and the engine are handled for you.
            </p>
          </Scene>

          {/* Quiet divider before the finale */}
          <Scene className="col-span-12" start="top 88%">
            <div className="colophon-rule" data-cinema>
              <span className="font-display text-caption italic text-ink-subtle">
                how it works, in three beats
              </span>
            </div>
          </Scene>

          {/* III — full-width finale */}
          <Scene as="article" className="col-span-12" stagger={0.12}>
            <div className="grid grid-cols-12 items-baseline gap-x-6 gap-y-3">
              <p
                data-cinema
                className="col-span-12 font-display text-h-display italic leading-none text-accent-deep md:col-span-2"
              >
                III.
              </p>
              <div data-cinema className="col-span-12 md:col-span-10">
                <h2 className="heading-section">Yours to keep</h2>
                <p className="mt-4 max-w-prose text-body leading-relaxed text-ink-soft">
                  Gildings save to your private gallery, post to the public feed
                  when you choose, and can be remixed, letting others cast their
                  own character into your scene, attribution carried through.
                </p>
              </div>
            </div>
          </Scene>
            </section>

            {/* Colophon — the landing route hides the global footer, so it lives here. */}
            <Colophon className="mt-28 md:mt-36" />
          </div>
        </>
      )}
    </>
  );
}

/* ============================================================
   Inline SVG icons — bespoke, not lucide. Live here until the
   icons/ folder is established by GenerationCard work.
   ============================================================ */

interface IconProps {
  className?: string;
}

/** Clamp to [0,1]. */
function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Smoothstep ramp from edge0→edge1 (eased 0→1). */
function smooth(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** A "rise in, hold, recede" envelope over [a,b]=in, [c,d]=out. */
function band(p: number, a: number, b: number, c: number, d: number): number {
  if (p <= a || p >= d) return 0;
  if (p < b) return smooth(a, b, p);
  if (p > c) return 1 - smooth(c, d, p);
  return 1;
}

/** Drive a beat overlay's opacity + drift from scroll progress. */
function driveBeat(
  el: HTMLDivElement | null,
  p: number,
  a: number,
  b: number,
  c: number,
  d: number,
): void {
  if (!el) return;
  const o = band(p, a, b, c, d);
  const dir = p < (b + c) / 2 ? 1 : -1;
  el.style.opacity = String(o);
  // Cinematic rack-focus: each beat rises, scales, and pulls into focus as it
  // arrives, then softens and recedes — far more alive than a flat opacity fade.
  const scale = (0.955 + 0.045 * o).toFixed(3);
  el.style.transform = `translateY(${(16 * (1 - o) * dir).toFixed(1)}px) scale(${scale})`;
  el.style.filter = o < 0.995 ? `blur(${((1 - o) * 7).toFixed(2)}px)` : 'none';
  // The gilded numeral rule draws in lockstep with the beat's reveal.
  const rule = el.querySelector('.beat-rule');
  if (rule instanceof HTMLElement) rule.style.transform = `scaleX(${o.toFixed(3)})`;
}

/**
 * GildWord — a word that gilds itself: matte un-leafed gold on entrance, then a
 * gold-leaf wipe lays live metallic foil across it (see `.gild-word` in
 * globals.css). `gated` holds it un-leafed until an ancestor gains `.is-arrived`,
 * so the sign-in card's line gilds when you reach the doorway, not at page load.
 */
function GildWord({
  children,
  className,
  gated = false,
}: {
  children: string;
  className?: string;
  gated?: boolean;
}) {
  return (
    <span className={cn('gild-word', gated && 'gild-word--gated', className)} data-text={children}>
      {children}
    </span>
  );
}

/** The gilded sign-in card — connect or take the free first gilding. */
function LoginCard({ busy, onConnect }: { busy: boolean; onConnect: () => void }) {
  return (
    <div className="relative w-full max-w-sm">
      {/* Warm halo — lifts the gilt-edged plate off the dark hall (in the wrapper,
          outside the card's overflow clip so it can spill past the edges). */}
      <div className="gild-plate-halo" aria-hidden="true" />
      <div className="theme-dark gild-plate relative overflow-hidden rounded-2xl text-ink">
        {/* One slow light-rake sweeps the plate face when you reach the doorway. */}
        <div className="gild-rake" aria-hidden="true" />
      <div className="relative px-7 py-8 text-center">
        <Fleuron className="mx-auto h-4 w-8" />
        <p className="mt-4 font-display text-lede italic leading-tight text-ink">Worth walking in for.</p>
        <p className="font-display text-lede italic leading-tight">
          <GildWord gated>Start here.</GildWord>
        </p>

        <div className="mt-7 flex flex-col items-stretch gap-3">
          <MagneticButton variant="primary" busy={busy} onClick={onConnect} className="w-full justify-center">
            Connect Wallet
            <span aria-hidden="true">&rarr;</span>
          </MagneticButton>
          <p className="font-mono text-caption text-ink-subtle">
            Sign-In With Ethereum · no gas, no email
          </p>
          {hasWallet() && (
            <p className="font-mono text-caption text-ink-subtle">
              {detectedWalletNames().join(' · ')} detected
            </p>
          )}

          <div className="my-1 flex items-center gap-3">
            <span aria-hidden="true" className="h-px flex-1 bg-hairline" />
            <span className="font-display text-caption italic text-ink-subtle">or</span>
            <span aria-hidden="true" className="h-px flex-1 bg-hairline" />
          </div>

          <MagneticButton variant="gold-ghost" to="/new" pull={0.22} className="w-full justify-center">
            <FreeSparkle className="text-accent transition-colors duration-300 group-hover:text-[#0b0a0f]" />
            Gild your first, free
            <span aria-hidden="true">&rarr;</span>
          </MagneticButton>
          <p className="font-mono text-caption text-ink-subtle">No wallet, no sign-up.</p>
        </div>
      </div>
      </div>
    </div>
  );
}

/** A slender gilded lozenge fleuron (matches the footer ornament). */
function Fleuron({ className }: IconProps) {
  return (
    <svg viewBox="0 0 28 16" aria-hidden="true" className={className ?? 'h-3.5 w-6'}>
      <defs>
        <linearGradient id="ornRampLanding" x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor="#a87f2c" />
          <stop offset="0.42" stopColor="#e9c873" />
          <stop offset="0.52" stopColor="#fff3ce" />
          <stop offset="0.64" stopColor="#c2922f" />
          <stop offset="1" stopColor="#7d521a" />
        </linearGradient>
      </defs>
      <path d="M2 8 L14 3.2 L26 8 L14 12.8 Z" fill="url(#ornRampLanding)" />
      <path d="M2 8 L26 8" stroke="#fff3ce" strokeWidth="0.5" opacity="0.65" />
      <circle cx="14" cy="8" r="1.1" fill="#fff3ce" />
    </svg>
  );
}

/** The colophon line — fleuron rule + the brand sign-off. */
function Colophon({ className }: IconProps) {
  return (
    <div className={cn('text-center', className)}>
      <div className="colophon-rule mb-2.5" aria-hidden="true">
        <Fleuron />
      </div>
      <p className="font-display italic text-caption text-ink-soft drop-shadow-[0_2px_14px_rgba(0,0,0,0.95)]">
        Gilding. Bring a sketch, leave with gold.
      </p>
      <nav
        aria-label="legal"
        className="pointer-events-auto mt-3 flex items-center justify-center gap-4 font-display text-micro uppercase tracking-[0.18em] text-ink-subtle"
      >
        <Link to="/privacy" className="transition-colors hover:text-ink-soft">
          Privacy
        </Link>
        <span aria-hidden="true" className="h-3 w-px bg-hairline" />
        <Link to="/terms" className="transition-colors hover:text-ink-soft">
          Terms
        </Link>
      </nav>
    </div>
  );
}

/** Scroll-progress bands [in, out] for the three beats (mirrors driveBeat) —
 *  used to decide which beat's sculpt is in view and which to keep mounted. */
const BEAT_BANDS: [number, number][] = [
  [0.18, 0.4],
  [0.42, 0.64],
  [0.66, 0.85],
];

interface ShowcaseItem {
  slug: string;
  id: string;
  cover: string;
  coverIndex: number;
  aspectRatio: string | null;
  depth: boolean;
}

const SHOWCASE_ASPECTS: Record<string, number> = {
  '1:1': 1,
  '3:2': 3 / 2,
  '2:3': 2 / 3,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
};

/** One showcase gilding as a live, transparent, auto-orbiting living sculpt,
 *  centred under its beat. ~Twice the size of the old baked clips. */
function SculptMedia({ item, paused }: { item: ShowcaseItem; paused: boolean }) {
  const aspect = SHOWCASE_ASPECTS[item.aspectRatio ?? ''] ?? 2 / 3;
  return (
    <div className="mt-7 flex justify-center">
      <LivingSculpt
        src={item.cover}
        spatialUrl={`https://api.gilding.app/images/spatial2/${item.id}-${item.coverIndex}`}
        aspect={aspect}
        paused={paused}
        ambient
        motionHint={false}
        heightCap="48svh"
        alt=""
        className="drop-shadow-[0_36px_70px_rgba(0,0,0,0.55)]"
      />
    </div>
  );
}

/** A feature beat that surfaces over the corridor as you scroll past a depth. */
function BeatOverlay({
  forwardRef,
  numeral,
  heading,
  body,
  media,
}: {
  forwardRef: RefObject<HTMLDivElement | null>;
  numeral: string;
  heading: string;
  body: string;
  /** Optional living-sculpt of the result; rides this beat's scroll fade with
   *  the text (it sits inside the beat element the fade is applied to). */
  media?: ReactNode;
}) {
  return (
    <div
      ref={forwardRef}
      style={{ opacity: 0 }}
      className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-6 text-center will-change-[opacity,transform]"
    >
      {/* Nudge the whole stack down so the numeral clears the fixed header and the
          frame sits closer to the foot — the content was riding too high. */}
      <div className="relative max-w-xl translate-y-[7vh] sm:translate-y-[3vh]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: '160%',
            height: '180%',
            background: 'radial-gradient(closest-side, rgba(0,0,0,0.6), rgba(0,0,0,0.28) 55%, transparent 80%)',
            filter: 'blur(8px)',
          }}
        />
        <p className="gild-numeral relative font-display text-h-display italic leading-none">
          {numeral}
        </p>
        <span
          aria-hidden="true"
          className="beat-rule relative mx-auto mt-3 block h-px w-16 rounded-full"
          style={{ background: 'linear-gradient(90deg,transparent,#e9c873,transparent)' }}
        />
        <h2 className="relative heading-section mt-3 text-ink drop-shadow-[0_3px_24px_rgba(0,0,0,0.95)]">
          {heading}
        </h2>
        <p className="relative mt-4 text-body leading-relaxed text-ink/95 drop-shadow-[0_2px_16px_rgba(0,0,0,0.98)]">
          {body}
        </p>

        {/* The result, in motion — a living sculpt of the gilding (its real 3D
            relief in a carved gold frame), auto-orbiting on a TRANSPARENT
            background so it floats over the corridor. It sits inside the beat
            element, so the scroll-driven opacity, drift, and blur fade it in and
            out in lockstep with the text. */}
        {media}
      </div>
    </div>
  );
}

function ScrollGlyph({ className }: IconProps) {
  // A slender gilded mouse with a falling wheel-dot — the "scroll" cue.
  return (
    <svg
      viewBox="0 0 14 22"
      width="13"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      aria-hidden="true"
      className={className}
    >
      <rect x="1.5" y="1.5" width="11" height="19" rx="5.5" />
      <line x1="7" y1="6" x2="7" y2="9.5" strokeLinecap="round">
        <animate attributeName="y1" values="6;10;6" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="y2" values="9.5;13.5;9.5" dur="1.8s" repeatCount="indefinite" />
      </line>
    </svg>
  );
}

function FreeSparkle({ className }: IconProps) {
  // A four-point gilded spark — a single struck leaf of gold, with a small
  // companion glint. Matches the manuscript ornament language (not a UI badge).
  return (
    <svg
      viewBox="0 0 18 18"
      width="16"
      height="16"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M9 1.4 c0.5 3.2 1.9 4.6 5.1 5.1 c-3.2 0.5 -4.6 1.9 -5.1 5.1 c-0.5 -3.2 -1.9 -4.6 -5.1 -5.1 c3.2 -0.5 4.6 -1.9 5.1 -5.1 Z" />
      <path d="M14.4 11.2 c0.2 1.2 0.7 1.7 1.9 1.9 c-1.2 0.2 -1.7 0.7 -1.9 1.9 c-0.2 -1.2 -0.7 -1.7 -1.9 -1.9 c1.2 -0.2 1.7 -0.7 1.9 -1.9 Z" opacity="0.8" />
    </svg>
  );
}

function Chevron({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M3 4.5 L6 7.5 L9 4.5" />
    </svg>
  );
}

function QuillDiagram({ className }: IconProps) {
  // A forged-gold fountain-pen nib — the instrument of amplification. Gradient
  // crown facets with a breather hole, slit, and rim light, set in a soft
  // halo, over a gilded flourish sealed with the brand lozenge. Self-contained
  // gold ramp (not currentColor), so it reads premium on parchment and void
  // alike. Lines use solid gold, never a gradient (gradients collapse on thin
  // strokes).
  return (
    <svg
      viewBox="0 0 96 120"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="qdBody" x1="0.15" y1="0" x2="0.85" y2="1">
          <stop offset="0" stopColor="#fff3ce" />
          <stop offset="0.28" stopColor="#e9c873" />
          <stop offset="0.6" stopColor="#c2922f" />
          <stop offset="1" stopColor="#7d521a" />
        </linearGradient>
        <radialGradient id="qdHalo" cx="0.5" cy="0.42" r="0.6">
          <stop offset="0" stopColor="#e9c873" stopOpacity="0.24" />
          <stop offset="1" stopColor="#e9c873" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="qdFlour" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#8a6020" stopOpacity="0" />
          <stop offset="0.5" stopColor="#e9c873" />
          <stop offset="1" stopColor="#8a6020" stopOpacity="0" />
        </linearGradient>
      </defs>

      <ellipse cx="48" cy="50" rx="46" ry="50" fill="url(#qdHalo)" />

      {/* nib body — shoulders at top, tapering to a fine point */}
      <path
        d="M 30 14 C 36 10, 60 10, 66 14 C 64 30, 60 52, 50 86 C 49 89, 47 89, 46 86 C 38 52, 32 30, 30 14 Z"
        fill="url(#qdBody)"
        stroke="#6e4814"
        strokeWidth="0.6"
        strokeOpacity="0.55"
      />
      {/* left facet highlight */}
      <path
        d="M 33 16 C 38 13, 46 12, 48 13 C 46 34, 47 60, 47.5 84 C 42 52, 36 30, 33 16 Z"
        fill="#fff3ce"
        opacity="0.34"
      />
      {/* breather hole */}
      <circle cx="48" cy="40" r="5.2" fill="#5e3e10" />
      <circle cx="48" cy="40" r="5.2" fill="none" stroke="#fff3ce" strokeWidth="0.7" strokeOpacity="0.55" />
      <circle cx="46.4" cy="38.4" r="1.5" fill="#fff3ce" opacity="0.7" />
      {/* slit + right-shoulder rim light */}
      <path d="M 48 45 L 48 84" stroke="#5e3e10" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M 66 14 C 64 30, 60 52, 50 86" stroke="#fff7e0" strokeWidth="0.7" strokeOpacity="0.5" fill="none" />

      {/* gilded flourish + lozenge seal */}
      <path d="M 16 104 C 34 98, 62 98, 80 104" stroke="url(#qdFlour)" strokeWidth="2" strokeLinecap="round" fill="none" />
      <g transform="translate(38,99)">
        <path d="M2 5 L10 2 L18 5 L10 8 Z" fill="url(#qdBody)" />
        <path d="M2 5 L18 5" stroke="#fff3ce" strokeWidth="0.4" opacity="0.7" />
        <circle cx="10" cy="5" r="0.8" fill="#fff3ce" />
      </g>

      {/* sparkle */}
      <path
        d="M 72 30 l1 2.8 2.8 1 -2.8 1 -1 2.8 -1 -2.8 -2.8 -1 2.8 -1 Z"
        fill="#fff7e0"
        opacity="0.92"
      />
    </svg>
  );
}
